import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronDown, ChevronRight, Megaphone, FileText, DollarSign, CalendarDays, Wrench, ScrollText, FileSignature, Shield } from "lucide-react";

interface OpStep {
  textEs: string;
  textEn: string;
}

interface Operation {
  id: string;
  icon: React.ReactNode;
  category: string;
  titleEs: string;
  titleEn: string;
  whoEs: string;
  whoEn: string;
  whenEs: string;
  whenEn: string;
  steps: OpStep[];
  noteEs?: string;
  noteEn?: string;
}

const OPERATIONS: Operation[] = [
  {
    id: "publish-announcement",
    icon: <Megaphone size={20} />,
    category: "Comunicaciones",
    titleEs: "Publicar un comunicado a los titulares",
    titleEn: "Publish an announcement to owners",
    whoEs: "Administrador o miembro de la Junta",
    whoEn: "Administrator or Board member",
    whenEs: "Cuando necesite informar a todos los titulares sobre mantenimientos, normas, asambleas, recordatorios financieros, etc.",
    whenEn: "When you need to inform all owners about maintenance, rules, assemblies, financial reminders, etc.",
    steps: [
      { textEs: "Vaya a Comunicados desde la barra lateral.", textEn: "Go to Announcements from the sidebar." },
      { textEs: "Haga clic en Nuevo Comunicado.", textEn: "Click New Announcement." },
      { textEs: "Complete título, categoría (General / Mantenimiento / Seguridad / Financiero / Regulatorio / Urgente), contenido y fecha de expiración opcional.", textEn: "Fill in title, category (General / Maintenance / Security / Financial / Regulatory / Urgent), content, and optional expiration date." },
      { textEs: "Agregue adjuntos por URL si es necesario (PDF, imágenes alojadas en Drive o Cloudinary).", textEn: "Add attachments by URL if needed (PDFs, images hosted in Drive or Cloudinary)." },
      { textEs: "Guarde como borrador.", textEn: "Save as draft." },
      { textEs: "Revíselo en la lista. Cuando esté listo, haga clic en Publicar y Enviar. Confirme.", textEn: "Review it in the list. When ready, click Publish and Send. Confirm." },
      { textEs: "El sistema envía un correo a todos los titulares con correo registrado y marca el comunicado como PUBLICADO.", textEn: "The system emails all owners with a registered email and marks the announcement as PUBLISHED." },
    ],
    noteEs: "Una vez publicado, no se puede volver a publicar (devuelve 409). Use Archivar para retirarlo de la vista de residentes.",
    noteEn: "Once published, it cannot be re-published (returns 409). Use Archive to remove it from the residents' view.",
  },
  {
    id: "approve-permit",
    icon: <FileText size={20} />,
    category: "Permisos",
    titleEs: "Aprobar una solicitud de permiso de obra",
    titleEn: "Approve a works permit request",
    whoEs: "Administrador o Junta",
    whoEn: "Administrator or Board",
    whenEs: "Cuando un residente somete una solicitud de remodelación, instalación o construcción dentro de su unidad.",
    whenEn: "When a resident submits a remodeling, installation, or construction request within their unit.",
    steps: [
      { textEs: "Reciba notificación por correo cuando el residente somete la solicitud.", textEn: "Receive email notification when the resident submits the request." },
      { textEs: "Vaya a Solicitudes de Titulares. Las solicitudes pendientes aparecen con estado SUBMITTED.", textEn: "Go to Works Permits. Pending requests appear with SUBMITTED status." },
      { textEs: "Haga clic en Revisar.", textEn: "Click Review." },
      { textEs: "Lea descripción, fechas, contratista y adjuntos. Si necesita más información, comuníquese con el titular antes.", textEn: "Read description, dates, contractor, and attachments. If you need more info, contact the owner first." },
      { textEs: "Agregue notas de revisión (horario laboral permitido, condiciones, etc.).", textEn: "Add review notes (allowed work hours, conditions, etc.)." },
      { textEs: "Haga clic en Aprobar (o Rechazar con justificación).", textEn: "Click Approve (or Reject with justification)." },
      { textEs: "El sistema genera un PDF de Resolución de Obras con número secuencial (ej. 2026-001) y envía correo al titular automáticamente.", textEn: "The system generates a Works Resolution PDF with sequential number (e.g. 2026-001) and emails the owner automatically." },
    ],
  },
  {
    id: "record-payment",
    icon: <DollarSign size={20} />,
    category: "Finanzas",
    titleEs: "Registrar el pago de una cuota",
    titleEn: "Record a fee payment",
    whoEs: "Administrador o Tesorera",
    whoEn: "Administrator or Treasurer",
    whenEs: "Cuando un titular paga por transferencia, ATH Móvil, cheque, efectivo o tarjeta y necesita registrarse en el sistema.",
    whenEn: "When an owner pays via transfer, ATH Móvil, check, cash, or card and needs to be recorded in the system.",
    steps: [
      { textEs: "Vaya a Finanzas → Cuotas.", textEn: "Go to Finance → Fees." },
      { textEs: "Localice la unidad y el período. Use la búsqueda si la lista es larga.", textEn: "Locate the unit and period. Use search if the list is long." },
      { textEs: "Haga clic en Marcar como Pagada.", textEn: "Click Mark as Paid." },
      { textEs: "Seleccione el método de pago.", textEn: "Select the payment method." },
      { textEs: "Ingrese la referencia de transacción (número de cheque, ID de ATH Móvil, etc.).", textEn: "Enter the transaction reference (check number, ATH Móvil ID, etc.)." },
      { textEs: "Guarde. El sistema cambia el estado a PAGADA y envía un recibo por correo al titular.", textEn: "Save. The system changes status to PAID and emails a receipt to the owner." },
    ],
    noteEs: "El sistema audita cada pago. Para corregir un error, debe crear una nota interna; no se puede revertir el estado directamente.",
    noteEn: "The system audits each payment. To correct a mistake, create an internal note; status cannot be reverted directly.",
  },
  {
    id: "generate-fees",
    icon: <DollarSign size={20} />,
    category: "Finanzas",
    titleEs: "Generar cuotas del mes para todas las unidades",
    titleEn: "Generate monthly fees for all units",
    whoEs: "Administrador",
    whoEn: "Administrator",
    whenEs: "Al inicio de cada mes, para crear las cuotas mensuales en masa.",
    whenEn: "At the start of each month, to bulk-create the monthly fees.",
    steps: [
      { textEs: "Vaya a Finanzas → Cuotas.", textEn: "Go to Finance → Fees." },
      { textEs: "Haga clic en Generar Cuotas del Mes.", textEn: "Click Generate Monthly Fees." },
      { textEs: "Ingrese el período en formato YYYY-MM (ej. 2026-05).", textEn: "Enter the period in YYYY-MM format (e.g. 2026-05)." },
      { textEs: "Confirme la fecha de vencimiento (primer día del mes).", textEn: "Confirm the due date (first day of the month)." },
      { textEs: "Haga clic en Generar. A cada unidad que no tenga cuota para ese período se le factura su cuota mensual (Unidades → Cuota mensual).", textEn: "Click Generate. Every unit without a fee for that period is billed its monthly fee (Units → Monthly fee)." },
    ],
    noteEs: "Es idempotente: re-ejecutar para el mismo período no duplica cuotas. Las unidades sin cuota mensual se omiten y se listan en el resultado; asígneles la cuota y vuelva a generar.",
    noteEn: "It's idempotent: re-running for the same period won't duplicate fees. Units without a monthly fee are skipped and listed in the result; set their fee and generate again.",
  },
  {
    id: "create-meeting",
    icon: <CalendarDays size={20} />,
    category: "Gobierno",
    titleEs: "Convocar una reunión de Junta con agenda",
    titleEn: "Call a Board meeting with agenda",
    whoEs: "Administrador o miembro de la Junta",
    whoEn: "Administrator or Board member",
    whenEs: "Para reuniones ordinarias mensuales o extraordinarias por asuntos urgentes.",
    whenEn: "For monthly ordinary meetings or extraordinary ones for urgent matters.",
    steps: [
      { textEs: "Vaya a Reuniones.", textEn: "Go to Meetings." },
      { textEs: "Haga clic en Nueva Reunión.", textEn: "Click New Meeting." },
      { textEs: "Complete título, tipo (Junta / Asamblea / Extraordinaria), fecha-hora, ubicación y enlace de Google Meet si aplica.", textEn: "Fill in title, type (Board / Assembly / Extraordinary), date-time, location, and Google Meet link if applicable." },
      { textEs: "Guarde la reunión.", textEn: "Save the meeting." },
      { textEs: "Notifique a la Junta por correo o WhatsApp con el enlace al portal.", textEn: "Notify the Board by email or WhatsApp with the portal link." },
    ],
    noteEs: "Los puntos de agenda iniciales se pueden definir al crear. Para editar después, abra el detalle de la reunión.",
    noteEn: "Initial agenda items can be defined at creation. To edit later, open the meeting detail.",
  },
  {
    id: "approve-minutes",
    icon: <ScrollText size={20} />,
    category: "Gobierno",
    titleEs: "Redactar y aprobar el acta de una reunión",
    titleEn: "Draft and approve meeting minutes",
    whoEs: "Secretaria de la Junta",
    whoEn: "Board Secretary",
    whenEs: "Después de cada reunión, para documentar y distribuir a los titulares.",
    whenEn: "After each meeting, to document and distribute to owners.",
    steps: [
      { textEs: "Abra la reunión en el detalle (clic en el título desde la lista).", textEn: "Open the meeting in detail (click the title from the list)." },
      { textEs: "En la sección Acta, redacte el contenido del borrador.", textEn: "In the Minutes section, draft the content." },
      { textEs: "Haga clic en Crear Acta. El sistema envía correo a todos los miembros de la Junta para revisión.", textEn: "Click Create Minutes. The system emails all Board members for review." },
      { textEs: "Los miembros de la Junta agregan comentarios inline desde el portal.", textEn: "Board members add inline comments from the portal." },
      { textEs: "La Secretaria edita el contenido final basado en los comentarios.", textEn: "The Secretary edits the final content based on the comments." },
      { textEs: "Haga clic en Aprobar y Distribuir. El sistema genera el PDF y envía correo a todos los titulares.", textEn: "Click Approve and Distribute. The system generates the PDF and emails all owners." },
    ],
    noteEs: "Una vez aprobada, el acta queda bloqueada. El PDF y la distribución son automáticos.",
    noteEn: "Once approved, the minutes are locked. PDF generation and distribution are automatic.",
  },
  {
    id: "vote-resolution",
    icon: <ScrollText size={20} />,
    category: "Gobierno",
    titleEs: "Crear y votar una resolución",
    titleEn: "Create and vote on a resolution",
    whoEs: "Junta de Directores",
    whoEn: "Board of Directors",
    whenEs: "Cuando la Junta necesita tomar una decisión formal que requiera votación documentada.",
    whenEn: "When the Board needs to make a formal decision requiring documented voting.",
    steps: [
      { textEs: "Vaya a Resoluciones.", textEn: "Go to Resolutions." },
      { textEs: "Haga clic en Nueva Resolución.", textEn: "Click New Resolution." },
      { textEs: "Complete título, descripción detallada y propuesta por.", textEn: "Fill in title, detailed description, and proposed by." },
      { textEs: "Guarde. La resolución se abre automáticamente en VOTACIÓN y se notifica a la Junta por correo.", textEn: "Save. The resolution auto-opens for VOTING and the Board is notified by email." },
      { textEs: "Cada miembro de la Junta entra al portal y vota A FAVOR / EN CONTRA / ABSTENCIÓN con un comentario opcional.", textEn: "Each Board member enters the portal and votes YES / NO / ABSTAIN with optional comment." },
      { textEs: "Cuando todos los miembros activos han votado, el sistema cambia automáticamente el estado a APROBADA o RECHAZADA según el conteo.", textEn: "When all active members have voted, the system auto-switches status to APPROVED or REJECTED based on the count." },
      { textEs: "Si es aprobada, se genera automáticamente un PDF con el conteo de votos. Una vez firmada en papel, súbala con «Subir resolución firmada».", textEn: "If approved, a PDF with the vote tally is auto-generated. Once signed on paper, upload it with “Upload signed resolution”." },
    ],
  },
  {
    id: "handle-maintenance",
    icon: <Wrench size={20} />,
    category: "Mantenimiento",
    titleEs: "Atender una solicitud de mantenimiento",
    titleEn: "Handle a maintenance request",
    whoEs: "Administrador",
    whoEn: "Administrator",
    whenEs: "Cuando un residente reporta un problema (plomería, eléctrico, ascensor, área común, etc.).",
    whenEn: "When a resident reports an issue (plumbing, electrical, elevator, common area, etc.).",
    steps: [
      { textEs: "Reciba notificación por correo con prioridad y categoría.", textEn: "Receive email notification with priority and category." },
      { textEs: "Vaya a Mantenimiento. Las solicitudes urgentes aparecen primero.", textEn: "Go to Maintenance. Urgent requests appear first." },
      { textEs: "Evalúe la solicitud y, si aplica, abra el detalle para ver fotos.", textEn: "Evaluate the request and, if applicable, open the detail to view photos." },
      { textEs: "Cambie el estado a ASIGNADA y asigne a personal de mantenimiento.", textEn: "Change status to ASSIGNED and assign to maintenance staff." },
      { textEs: "Cuando el personal comienza, cambie a EN PROCESO.", textEn: "When staff starts, change to IN PROGRESS." },
      { textEs: "Al terminar, cambie a RESUELTA. El sistema le pedirá notas de resolución.", textEn: "When finished, change to RESOLVED. The system will prompt for resolution notes." },
      { textEs: "El residente recibe correo en cada cambio de estado.", textEn: "The resident receives email at each status change." },
    ],
  },
  {
    id: "manage-contract",
    icon: <FileSignature size={20} />,
    category: "Legal",
    titleEs: "Gestionar la renovación de un contrato",
    titleEn: "Manage a contract renewal",
    whoEs: "Administrador o Presidente",
    whoEn: "Administrator or President",
    whenEs: "Cuando el sistema alerta que un contrato vencerá en 90, 60 o 30 días.",
    whenEn: "When the system alerts that a contract will expire in 90, 60, or 30 days.",
    steps: [
      { textEs: "Reciba alerta automática por correo (diaria, en los umbrales 90/60/30 días).", textEn: "Receive automatic email alert (daily, at 90/60/30 day thresholds)." },
      { textEs: "Vaya a Contratos. El contrato aparece resaltado en rojo/ámbar/amarillo.", textEn: "Go to Contracts. The contract is highlighted in red/amber/yellow." },
      { textEs: "Evalúe condiciones y consulte cotizaciones de proveedores alternativos.", textEn: "Evaluate conditions and review quotes from alternative vendors." },
      { textEs: "Discuta en reunión de Junta y, si aplica, cree una Resolución de renovación.", textEn: "Discuss in Board meeting and, if applicable, create a renewal Resolution." },
      { textEs: "Una vez negociado, cree un nuevo contrato con las nuevas fechas y suba el PDF firmado.", textEn: "Once negotiated, create a new contract with the new dates and upload the signed PDF." },
      { textEs: "Marque el contrato anterior como TERMINADO o deje que expire automáticamente.", textEn: "Mark the previous contract as TERMINATED or let it expire automatically." },
    ],
  },
  {
    id: "report-incident",
    icon: <Shield size={20} />,
    category: "Seguridad",
    titleEs: "Reportar y dar seguimiento a un incidente",
    titleEn: "Report and follow up on an incident",
    whoEs: "Administrador o miembro de la Junta",
    whoEn: "Administrator or Board member",
    whenEs: "Cuando ocurre un evento de seguridad: daño, allanamiento, robo, accidente, violación de reglas.",
    whenEn: "When a security event occurs: damage, trespassing, theft, accident, rule violation.",
    steps: [
      { textEs: "Vaya a Incidentes → Nuevo Incidente.", textEn: "Go to Incidents → New Incident." },
      { textEs: "Seleccione tipo, ubicación específica y unidad involucrada si aplica.", textEn: "Select type, specific location, and involved unit if applicable." },
      { textEs: "Describa los hechos con detalle.", textEn: "Describe the facts in detail." },
      { textEs: "Adjunte fotos (URLs) si las tiene.", textEn: "Attach photos (URLs) if available." },
      { textEs: "Guarde. El incidente queda en estado ABIERTO.", textEn: "Save. The incident is in OPEN status." },
      { textEs: "Cambie a INVESTIGANDO mientras se gestiona con seguridad, policía o aseguradora.", textEn: "Change to INVESTIGATING while managing with security, police, or insurance." },
      { textEs: "Al concluir, cambie a RESUELTO o CERRADO con notas de resolución.", textEn: "When concluded, change to RESOLVED or CLOSED with resolution notes." },
    ],
  },
  {
    id: "upload-doc-version",
    icon: <FileText size={20} />,
    category: "Documentos",
    titleEs: "Subir una nueva versión de un documento (Reglamento, Manual, etc.)",
    titleEn: "Upload a new version of a document (Bylaws, Manual, etc.)",
    whoEs: "Administrador o Junta",
    whoEn: "Administrator or Board",
    whenEs: "Cuando se actualiza el Reglamento, Manual de Normas, presupuesto u otros documentos oficiales.",
    whenEn: "When the Bylaws, Rules Manual, budget, or other official documents are updated.",
    steps: [
      { textEs: "Vaya a Documentos → Subir Documento.", textEn: "Go to Documents → Upload Document." },
      { textEs: "Use el MISMO título que la versión anterior (ej. \"Reglamento del Condominio\").", textEn: "Use the SAME title as the previous version (e.g. \"Condominium Bylaws\")." },
      { textEs: "Seleccione el MISMO tipo de documento.", textEn: "Select the SAME document type." },
      { textEs: "Incremente la versión (ej. 2.0 → 2.1).", textEn: "Increment the version (e.g. 2.0 → 2.1)." },
      { textEs: "Suba el archivo PDF.", textEn: "Upload the PDF file." },
      { textEs: "Establezca fecha de vigencia y estado de registro en el Registro de la Propiedad.", textEn: "Set effective date and registry status at the Property Registry." },
      { textEs: "Guarde. La versión anterior se marca automáticamente como NO ACTUAL.", textEn: "Save. The previous version is automatically marked as NOT CURRENT." },
    ],
    noteEs: "Use \"Ver Historial\" para consultar versiones anteriores. Solo el Administrador puede borrar versiones permanentemente.",
    noteEn: "Use \"View History\" to consult previous versions. Only the Administrator can permanently delete versions.",
  },
];

const CATEGORIES = ["Todas", "Comunicaciones", "Permisos", "Finanzas", "Gobierno", "Mantenimiento", "Legal", "Seguridad", "Documentos"];

export function HelpOperationsPage() {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const t = (es: string, en: string) => (isEn ? en : es);

  const [openIds, setOpenIds] = useState<Set<string>>(new Set([OPERATIONS[0].id]));
  const [category, setCategory] = useState("Todas");

  const filtered = category === "Todas" ? OPERATIONS : OPERATIONS.filter((o) => o.category === category);

  function toggle(id: string) {
    setOpenIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Link to="/help" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
        <ArrowLeft size={14} /> {t("Centro de Ayuda", "Help Center")}
      </Link>

      <div>
        <h1 className="text-2xl font-bold">{t("Operaciones Típicas", "Typical Operations")}</h1>
        <p className="text-slate-600 mt-1">
          {t(
            "Guías paso a paso para los flujos de trabajo más comunes del condominio.",
            "Step-by-step guides for the most common condominium workflows."
          )}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            className={`px-3 py-1 rounded-full text-sm transition ${
              category === c
                ? "bg-primary text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((op) => {
          const open = openIds.has(op.id);
          const steps = op.steps;
          return (
            <div key={op.id} className="card p-0 overflow-x-auto">
              <button
                onClick={() => toggle(op.id)}
                className="w-full p-4 flex items-center gap-3 hover:bg-slate-50 transition text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary flex items-center justify-center shrink-0">
                  {op.icon}
                </div>
                <div className="flex-1">
                  <div className="text-xs text-slate-500">{op.category}</div>
                  <div className="font-semibold">{isEn ? op.titleEn : op.titleEs}</div>
                </div>
                {open ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
              </button>

              {open && (
                <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 mt-3">
                    <div className="text-sm">
                      <div className="text-xs font-semibold uppercase text-slate-500 mb-1">{t("Quién", "Who")}</div>
                      <div>{isEn ? op.whoEn : op.whoEs}</div>
                    </div>
                    <div className="text-sm">
                      <div className="text-xs font-semibold uppercase text-slate-500 mb-1">{t("Cuándo", "When")}</div>
                      <div>{isEn ? op.whenEn : op.whenEs}</div>
                    </div>
                  </div>

                  <div className="text-xs font-semibold uppercase text-slate-500 mb-2">{t("Pasos", "Steps")}</div>
                  <ol className="space-y-3">
                    {steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <div className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center shrink-0">
                          {i + 1}
                        </div>
                        <div className="text-sm pt-0.5">{isEn ? s.textEn : s.textEs}</div>
                      </li>
                    ))}
                  </ol>

                  {(op.noteEs || op.noteEn) && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                      <div className="text-xs font-semibold uppercase text-amber-700 mb-1">{t("Nota", "Note")}</div>
                      <div className="text-amber-900">{isEn ? op.noteEn : op.noteEs}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
